import React, { useState, useRef, useEffect } from "react";
import { FleetrifyBgs } from "../../../assets/video";
import { Link } from "react-router-dom";
import { FLEETRIFYROUTEPATH } from "../../../routes/routesPath";
import { RiAccountCircleFill as RiAccountCircleFillRaw } from "react-icons/ri";
import { ArrowRight } from "lucide-react";
import CarouselCard from "../../../components/Carousel";
import { useCompanyProfile } from "../../../controllers/useCompanyProfile";

const RiAccountCircleFill = RiAccountCircleFillRaw as unknown as React.FC<
  React.SVGProps<SVGSVGElement>
>;

interface Option {
  id: string;
  name: string;
}

interface DropdownProps {
  options: Option[];
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
  maxVisibleItems: number;
  disabled?: boolean;
}

const CustomDropdown: React.FC<DropdownProps> = ({
  options,
  value,
  onChange,
  placeholder,
  maxVisibleItems,
  disabled,
}) => {
  const [open, setOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const containerRef = useRef<HTMLDivElement>(null);

  const handleClickOutside = (e: MouseEvent) => {
    if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
      setOpen(false);
      setSearchTerm("");
    }
  };

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const selectedOption = options.find((o) => o.id === value);

  const filteredOptions = options.filter((o) =>
    o.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="relative w-full" ref={containerRef}>
      <button
        type="button"
        disabled={disabled}
        className={`w-full bg-gray-100 border border-gray-500 text-[#4F4F4F] rounded-md px-5 py-3 text-[16px] text-left flex justify-between items-center ${disabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer"
          }`}
        onClick={() => setOpen((prev) => !prev)}
      >
        {selectedOption ? selectedOption.name : placeholder}
        <svg className="w-5 h-5 text-[#4F4F4F]" fill="currentColor" viewBox="0 0 20 20">
          <path
            fillRule="evenodd"
            d="M10 12a1 1 0 01-.707-.293l-4-4a1 1 0 011.414-1.414L10 9.586l3.293-3.293a1 1 0 111.414 1.414l-4 4A1 1 0 0110 12z"
            clipRule="evenodd"
          />
        </svg>
      </button>

      {open && (
        <div
          className="absolute z-50 w-full bg-white border border-gray-300 mb-1 rounded-md shadow-lg overflow-hidden"
          style={{
            maxHeight: `${maxVisibleItems * 30 + 40}px`,
            bottom: "100%",
            top: "auto",
          }}
        >
          {/* search input */}
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search..."
            className="w-full px-3 py-2 border-b border-gray-300 focus:outline-none focus:ring-1 focus:ring-blue-500"
          />

          {/* options list */}
          <ul className="overflow-y-auto" style={{ maxHeight: `${maxVisibleItems * 30}px` }}>
            {filteredOptions.length > 0 ? (
              filteredOptions.map((o) => (
                <li
                  key={o.id}
                  className="px-5 py-2 hover:bg-blue-100 cursor-pointer text-[#4F4F4F]"
                  onClick={() => {
                    onChange(o.id);
                    setOpen(false);
                    setSearchTerm("");
                  }}
                >
                  {o.name}
                </li>
              ))
            ) : (
              <li className="px-5 py-2 text-gray-400">No results found</li>
            )}
          </ul>
        </div>
      )}
    </div>
  );
};

const CompanyProfile: React.FC = () => {
  const slides = [
    { title: "Welcome to Fleetrify", description: "Login to manage logistics and fleet operations" },
    { title: "Real-time Fleet Insights", description: "Get live updates and analytics on every vehicle in your fleet" },
    { title: "Simplify Logistics Management", description: "Automate routing, tracking, and reporting — all in one platform" },
  ];

  const {
    companyName,
    setCompanyName,
    companyAddress,
    setCompanyAddress,
    companyPhone,
    setCompanyPhone,
    supportEmail,
    setSupportEmail,
    selectedCountry,
    setSelectedCountry,
    selectedState,
    setSelectedState,
    countries,
    states,
    countriesLoading,
    statesLoading,
    companyLogo,
    setCompanyLogo,
    saveStep1,
  } = useCompanyProfile();

  const renderForm = () => (
    <div className="w-full max-w-xl bg-white lg:bg-transparent lg:p-0 lg:shadow-none rounded-lg p-5 shadow-lg">
      <h2 className="text-center text-[24px] font-bold mt-4 text-[#143887]">COMPANY'S PROFILE</h2>
      <p className="text-center text-[16px] font-bold text-[#4F4F4F] mb-5">Set up your company's profile</p>

      {/* logo upload */}
      <div className="flex justify-center items-center flex-col gap-3 mb-4">
        <input
          type="file"
          accept="image/*"
          id="companyLogoInput"
          className="hidden"
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) setCompanyLogo(file);
          }}
        />

        {companyLogo ? (
          <img
            src={companyLogo instanceof File ? URL.createObjectURL(companyLogo) : companyLogo}
            alt="Company Logo"
            className="w-[128.5px] h-[128.5px] rounded-full object-cover cursor-pointer"
            onClick={() => document.getElementById("companyLogoInput")?.click()}
          />
        ) : (
          <RiAccountCircleFill
            className="w-[128.5px] h-[128.5px] text-[#143887] hover:text-blue-800 cursor-pointer"
            onClick={() => document.getElementById("companyLogoInput")?.click()}
          />
        )}
      </div>

      {/* inputs */}
      <div className="mt-2 space-y-4 w-full">
        <div>
          <label className="text-[16px] font-bold text-[#000000]">Company's Name</label>
          <input
            type="text"
            value={companyName}
            onChange={(e) => setCompanyName(e.target.value)}
            placeholder="Enter company's name"
            className="w-full border border-[#1438874D] mt-2 mb-2 text-[#4F4F4F] bg-white rounded-md px-5 py-3 text-[16px] focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>

        <div>
          <label className="text-[16px] font-bold text-[#000000]">Company's Address</label>
          <input
            type="text"
            value={companyAddress}
            onChange={(e) => setCompanyAddress(e.target.value)}
            placeholder="Enter address"
            className="w-full bg-white rounded-md border mt-2 mb-2 text-[#4F4F4F] border-[#1438874D] px-5 py-3 text-[16px] focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>

        <div>
          <label className="text-[16px] font-bold text-[#000000]">Phone Number</label>
          <input
            type="text"
            value={companyPhone}
            onChange={(e) => setCompanyPhone(e.target.value)}
            placeholder="Enter phone number"
            className="w-full bg-white rounded-md border mt-2 mb-2 text-[#4F4F4F] border-[#1438874D] px-5 py-3 text-[16px] focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>

        <div>
          <label className="text-[16px] font-bold text-[#000000]">Support Email</label>
          <input
            type="email"
            value={supportEmail}
            onChange={(e) => setSupportEmail(e.target.value)}
            placeholder="Enter support email"
            className="w-full bg-white rounded-md border mt-2 mb-2 text-[#4F4F4F] border-[#1438874D] px-5 py-3 text-[16px] focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>

        {/* country and state */}
        <div className="flex flex-col md:flex-row gap-6 w-full">
          <div className="flex flex-col flex-1 min-w-[200px]">
            <p className="text-[#000000] font-bold text-[16px]">Country</p>
            <CustomDropdown
              options={countries || []}
              value={selectedCountry}
              onChange={setSelectedCountry}
              placeholder={countriesLoading ? "Loading..." : "Select Country"}
              maxVisibleItems={8}
            />
          </div>

          <div className="flex flex-col flex-1 min-w-[200px]">
            <p className="text-[#000000] font-bold text-[16px]">State</p>
            <CustomDropdown
              options={states || []}
              value={selectedState}
              onChange={setSelectedState}
              placeholder={!selectedCountry ? "Select Country first" : "Select State"}
              maxVisibleItems={8}
              disabled={!selectedCountry}
            />
          </div>
        </div>
      </div>

      {/* buttons */}
      <div className="flex flex-row gap-5 w-full mt-5 mb-3">
        <Link
          to={FLEETRIFYROUTEPATH.COMPANYPROFILESETUP}
          className="flex-1 bg-[#143887] text-center text-white py-4 rounded-md hover:bg-blue-800"
          onClick={() => saveStep1()}
        >
          Next
        </Link>

        {/* <Link
          to={FLEETRIFYROUTEPATH.LOGIN}
          className="flex-1 bg-white text-blue-700 border border-[#143887] py-3 rounded-md hover:bg-[#143887] hover:text-white flex items-center justify-center gap-2"
        >
          <span>Skip</span>
          <ArrowRight className="w-4 h-4" />
        </Link> */}
      </div>
    </div>
  );

  return (
    <div className="flex flex-col lg:flex-row min-h-screen">
      {/* video section */}
      <div className="relative w-full lg:w-[45%] h-screen lg:sticky top-0 flex-shrink-0">
        <video className="absolute inset-0 w-full h-full object-cover" autoPlay muted loop playsInline>
          <source src={FleetrifyBgs} type="video/mp4" />
        </video>
        <div className="absolute inset-0 bg-black opacity-50 z-10"></div>

        <CarouselCard slides={slides} />

        {/* mobile and tablet form */}
        <div className="absolute inset-0 flex justify-center items-center z-30 px-5 md:px-10 lg:hidden">
          {renderForm()}
        </div>
      </div>

      {/* desktop form */}
      <div className="hidden lg:flex lg:w-[55%] lg:p-10 lg:items-center lg:justify-center bg-white">
        {renderForm()}
      </div>
    </div>
  );
};

export default CompanyProfile;
